import {useCallback, useEffect, useRef, useState} from "react";
import {apiFetch} from "../../../api/client";

interface Setting {
    account_code: string; enabled: boolean; target_limit: number | null; state: string;
    active_count: number | null; reserved_count: number | null; effective_limit: number | null;
    checked_at: string | null;
}
interface SettingsResponse {available: boolean; read_only: boolean; auto_fill_allowed: boolean; items: Setting[]}

const states: Record<string, string> = {
    disabled: "Выключен", waiting: "Ожидает проверки", checking: "Проверяем доступность",
    no_offers: "Нет подходящих предложений", at_limit: "Лимит достигнут",
    checking_assignment: "Проверяем принятие", account_disabled: "Приём кабинета выключен",
    source_unavailable: "Paylord не разрешает приём", account_unavailable: "Кабинет недоступен",
    read_failed: "Не удалось прочитать Paylord", capacity_unknown: "Лимит или загрузка не подтверждены",
    storage_failed: "Не удалось сохранить состояние", batch_limit: "Продолжим в следующем проходе",
    offer_unavailable: "Предложение уже недоступно",
    offers_skipped: "Предложения есть, но не подошли", pool_read_failed: "Не удалось прочитать пул",
};

export function PaylordPoolSettings({token, accountCode}: {token: string | null; accountCode: string}): JSX.Element | null {
    const [data, setData] = useState<SettingsResponse | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [target, setTarget] = useState("");
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const sequence = useRef(0);
    const saving = useRef(false);
    const endpoint = "/paylord/pool/settings";
    const refresh = useCallback(async () => {
        if (!token || saving.current) return;
        const request = ++sequence.current;
        try {
            const next = await apiFetch<SettingsResponse>(endpoint, token);
            if (request !== sequence.current) return;
            setData(next);
            setError("");
        } catch { if (request === sequence.current) setError("Не удалось загрузить настройки автодобора."); }
    }, [token]);
    useEffect(() => { saving.current = false; setBusy(false); setDirty(false); setData(null); setNotice(""); void refresh(); const timer = window.setInterval(() => void refresh(), 30000); return () => {++sequence.current;window.clearInterval(timer);}; }, [refresh, accountCode]);
    const current = data?.items.find(item => item.account_code === accountCode);
    useEffect(() => {
        if (current && !dirty) { setEnabled(current.enabled); setTarget(current.target_limit === null ? "" : String(current.target_limit)); }
    }, [current, dirty]);
    if (!token || (data && !data.available)) return null;
    const cannotEnable = !data?.auto_fill_allowed || !!data?.read_only;
    const save = async () => {
        const number = target.trim() === "" ? null : Number(target);
        if (number !== null && (!Number.isInteger(number) || number < 1 || number > 1000)) {
            setError("Введите целое число от 1 до 1000 или оставьте поле пустым."); return;
        }
        setBusy(true); setError(""); setNotice("");
        saving.current = true;
        const request = ++sequence.current;
        try {
            const next = await apiFetch<SettingsResponse>(`${endpoint}/${encodeURIComponent(accountCode)}`, token, {
                method: "PUT", body: JSON.stringify({enabled, target_limit: number}),
            });
            if (request !== sequence.current) return;
            setData(next); setDirty(false); setNotice("Настройки автодобора сохранены.");
        } catch { if (request === sequence.current) setError("Не удалось подтвердить сохранение. Обновите настройки перед повтором."); }
        finally { if (request === sequence.current) {saving.current = false;setBusy(false);} }
    };
    return <div className="merchant-rate-block" aria-label="Автодобор Paylord" style={{display: "flex", flexDirection: "column", gap: 12}}>
        <h3>Автодобор из общего пула</h3>
        <p className="section-hint">Принимаем доступные выплаты на этот кабинет до лимита. После подтверждения они появляются в обычном пуле Payments. Фильтра по курсу пока нет.</p>
        {data?.read_only && <p role="status">Режим только чтения: отправка на Paylord заблокирована.</p>}
        {data && !data.read_only && !data.auto_fill_allowed && <p>Автодобор ещё не разрешён на этом сервере.</p>}
        {error && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        {current && <>
            <p>Состояние: <strong>{states[current.state] ?? "Требуется проверка"}</strong></p>
            <p>Активных: {current.active_count ?? "—"} · Ещё не учтённых площадкой принятий: {current.reserved_count ?? "—"} · Лимит: {current.effective_limit ?? "—"}</p>
            {current.checked_at && <p className="section-hint">Проверено: {new Date(current.checked_at).toLocaleString()}</p>}
            <form className="merchant-external-form" style={{display: "grid", gap: 16}} onSubmit={e => {e.preventDefault(); void save();}}>
                <label className="form-field"><span style={{display: "inline-flex", alignItems: "center", gap: 8}}><input type="checkbox" checked={enabled} disabled={busy || (cannotEnable && !current.enabled)} onChange={e => {setEnabled(e.target.checked); setDirty(true);}}/> Включить автодобор</span></label>
                <label className="form-field" style={{display: "grid", gap: 8}}><span>Максимум активных заявок</span><input type="number" min="1" max="1000" step="1" value={target} placeholder="Лимит Paylord" disabled={busy || cannotEnable} onChange={e => {setTarget(e.target.value);setDirty(true);}}/>
                    <small>Пусто — используем лимит Paylord. Значение здесь может только уменьшить его. Если конечный лимит неизвестен, приём остановлен.</small></label>
                <div className="section-header-actions"><button className="btn btn-primary btn-sm" type="submit" disabled={busy || !dirty || (enabled && cannotEnable)}>Сохранить</button>
                    <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => {setDirty(false);void refresh();}}>Обновить</button></div>
            </form>
        </>}
    </div>;
}
